import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { mutate } from "swr";

import { MainButton, MainButtons } from "../../../../components/button";
import useCompetition from "../../../../hooks/useCompetition";
import useCurrentUser from "../../../../hooks/useCurrentUser";
import useLocalStorage from "../../../../hooks/useLocalStorage";
import Panel from "../Panel";
import { Heading, Intro } from "../Typography";
import { getIsLoggedIn } from "../../../../services/auth";
import { exchangeSingleUseAuthCode } from "../../../../api";
import { removeAuthCodeFromUrl } from "../hooks/useAuthWall";

const InitialEntryScreen = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { competition } = useCompetition(params.competitionUUID);
  const { profile, isValidating: isProfileValidating } = useCurrentUser();

  const [isParent] = useLocalStorage(
    `${params.competitionUUID}/isParent`,
    undefined
  );

  const [isWaitingForLogin, setIsWaitingForLogin] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const authCode = queryParams.get("authCode");
    if (authCode) {
      const currentUrl =
        queryParams.get("authCodeUrl") ||
        removeAuthCodeFromUrl(window.location.toString());
      setIsWaitingForLogin(true);
      exchangeSingleUseAuthCode(authCode, currentUrl)
        .then(() => {
          mutate("/profiles/me");
          mutate(`/competitions/${params.competitionUUID}`);
          setIsWaitingForLogin(false);
        })
        .catch(() => {
          setIsWaitingForLogin(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      (profile && competition?.userEntry) ||
      competition?.childEntries.length
    ) {
      navigate(`/${competition.uuid}`, { replace: true });
    } else if (profile && competition) {
      if (isParent !== undefined) {
        if (isParent) {
          navigate(`/${competition.uuid}/enter/competitors`, { replace: true });
        } else {
          navigate(`/${competition.uuid}/enter/confirm`, { replace: true });
        }
      }
    } else if (competition?.settings.hasPreloadedEntries) {
      navigate(`/${competition.uuid}/enter/competitor-number`);
    }
  }, [competition, profile, navigate, isParent]);

  return (
    <Panel>
      <Heading>
        {competition ? `Welcome to ${competition.name}` : <br />}
      </Heading>
      {!competition ||
      (!profile && isProfileValidating) ||
      isWaitingForLogin ? (
        <div className="d-flex justify-content-center">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {competition.settings.parentModeEnabled ? (
            <Intro>
              Are you competing yourself? Or are you a parent registering
              competitors?
            </Intro>
          ) : (
            <Intro>Let’s get you registered as a competitor!</Intro>
          )}
          <MainButtons>
            <MainButton
              onClick={() =>
                profile
                  ? navigate(`/${competition.uuid}/enter/confirm`)
                  : navigate("../email", { state: { isParent: false } })
              }
            >
              {competition.settings.parentModeEnabled
                ? "I’m competing"
                : "Get started"}
            </MainButton>
            {competition.settings.parentModeEnabled ? (
              <MainButton
                variant="outline-primary"
                onClick={() =>
                  profile
                    ? navigate(`/${competition.uuid}/enter/competitors`)
                    : navigate("../email", { state: { isParent: true } })
                }
              >
                I’m a parent
              </MainButton>
            ) : null}
          </MainButtons>
          {competition.settings.parentModeEnabled ? (
            <div className="fw-bold text-muted small mt-3 d-flex align-items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-info-circle-fill me-1"
                viewBox="0 0 16 16"
              >
                <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
              </svg>
              If you’re under 10, please get a parent or guardian to register
              you.
            </div>
          ) : null}
        </>
      )}
    </Panel>
  );
};

export default InitialEntryScreen;
